import apiV2 from '../api/v2';

// Mirrors odo-integrations' patients.analysis row (PatientAnalysis prisma
// model), served by the v2 backend at /patients/:patientId/analysis - reads
// come straight from the DB; creation is proxied to odo-integrations.
export type AnalysisStatus = 'pending' | 'success' | 'error';
export type HealthLabel = 'on_track' | 'needs_attention' | 'at_risk';
export type WaitingOn = 'doctor' | 'odo' | 'none' | 'unclear';

export interface AnalysisResult {
    tldr: string;
    recap: string[];
    action_items: string[];
    analysis: string;
    waiting_on: WaitingOn;
    waiting_reason: string;
}

export interface PatientAnalysis {
    id: string;
    patientId: string;
    status: AnalysisStatus;
    result: AnalysisResult | null;
    healthScore: number | null;
    healthLabel: HealthLabel | null;
    model: string | null;
    error: string | null;
    requestedBy: string | null;
    createdAt: string;
    updatedAt: string;
    completedAt: string | null;
}

export interface TriggerAnalysisResponse {
    analysis_id: string;
    status: AnalysisStatus;
    reused?: boolean;
}

export async function fetchPatientAnalyses(patientId: string): Promise<PatientAnalysis[]> {
    const response = await apiV2.get<{ success: boolean; data: PatientAnalysis[] }>(
        `/patients/${patientId}/analysis`
    );
    return response.data.data;
}

export async function triggerPatientAnalysis(patientId: string): Promise<TriggerAnalysisResponse> {
    const response = await apiV2.post<{ success: boolean; data: TriggerAnalysisResponse }>(
        `/patients/${patientId}/analysis`
    );
    return response.data.data;
}

/**
 * Extract the cooldown delay from a rejected trigger call. The v2 backend
 * answers 429 with { success: false, error: { details: { retry_after_ms } } }
 * while the last successful run is within the cooldown window. Null when the
 * failure is not a cooldown.
 */
export function cooldownMsFromError(err: unknown): number | null {
    if (typeof err !== 'object' || err === null) return null;
    const response = (
        err as {
            response?: {
                status?: number;
                data?: { error?: { details?: { retry_after_ms?: unknown } } };
            };
        }
    ).response;
    if (!response || response.status !== 429) return null;
    const retryAfterMs = response.data?.error?.details?.retry_after_ms;
    return typeof retryAfterMs === 'number' ? retryAfterMs : 0;
}
